import { Trans, useTranslation } from 'react-i18next';
import styles from './FilterSummary.module.scss';
import Skeleton from 'react-loading-skeleton';
import { VdsButton } from '@volt/volt-extended-components-react';
import { useAppDispatch, useAppSelector } from '@casops/store/hooks';
import { useCallback } from 'react';
import { clearFiltersSubset } from '@casops/store/slices/filtersSlice';
import { FilterProps } from '@casops/filters/types';
import { useHasSavedFilters } from '@casops/filters/useHasFilters';
import { ModelName } from '@casops/store/api';

export interface FilterSummaryProps extends FilterProps {
  modelName: ModelName;
  secondaryModelName?: string;
}

export function FilterSummary({
  countFilter,
  secondaryModelName,
  countAll,
  modelName,
  filterGroup,
}: FilterSummaryProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const dateTimeFormat = useAppSelector(state => state.settings.localization.dateTimeNumberFormat);

  const hasFilters = useHasSavedFilters(filterGroup);

  const canClearFilters = typeof filterGroup !== 'undefined' && hasFilters;
  const isDataLoaded = typeof countAll === 'number';
  const isVerbose = typeof countFilter === 'number' && countFilter !== countAll;
  const actualModelName = secondaryModelName ?? modelName;

  const onClearFilters = useCallback(() => {
    if (canClearFilters) {
      dispatch(clearFiltersSubset({ filterGroup }));
    }
  }, [dispatch, filterGroup, canClearFilters]);

  return (
    <div className={styles.filterSummary}>
      <div className={styles.filterSummary__text}>
        {isDataLoaded ? (
          isVerbose ? (
            <Trans
              i18nKey={'Model.Collection.FilteredShowing'}
              values={{
                countFilter: countFilter,
                count: countAll,
                modelName: actualModelName,
              }}
              components={{ b: <strong /> }}
              tOptions={{
                formatParams: {
                  countAll: { lng: dateTimeFormat },
                  countFilter: { lng: dateTimeFormat },
                },
              }}
            />
          ) : (
            <Trans
              i18nKey={'Model.Collection.Filtered'}
              values={{ count: countAll, modelName: actualModelName }}
              components={{ b: <strong /> }}
              tOptions={{ formatParams: { count: { lng: dateTimeFormat } } }}
            />
          )
        ) : (
          <Skeleton width={150} height="22px" />
        )}
      </div>

      {canClearFilters && (
        <VdsButton
          className={styles.clearFilterButton}
          onClick={onClearFilters}
          variant="ghost"
          size="medium"
        >
          {t('ClearFilters')}
        </VdsButton>
      )}
    </div>
  );
}

export default FilterSummary;
